/** Monoscopic WebRTC camera screens in the XR scene; never owns/stops RTC tracks. */
import {
  CanvasTexture, Group, Mesh, MeshBasicMaterial, PlaneGeometry, SRGBColorSpace, VideoTexture,
  Quaternion, Vector3,
} from "three";
import { createWindowLayout } from "./window-layout";

export interface XrVideoSource {
  slot: number;
  label: string;
  stream: MediaStream | null;
  enabled: boolean;
}

export interface XrVideoDiagnostics {
  slot: number;
  label: string;
  hasStream: boolean;
  readyState: number;
  paused: boolean;
  width: number;
  height: number;
  frames: number;
  live: boolean;
  error: string | null;
}

export const XR_VIDEO_STALE_MS = 700;

function makePanel(width: number, height: number, x: number, y: number) {
  const group = new Group();
  group.position.set(x, y, -1.6);
  const geometry = new PlaneGeometry(1, 1);
  const backingMaterial = new MeshBasicMaterial({ color: 0x17212b });
  const backing = new Mesh(geometry, backingMaterial);
  backing.scale.set(width, height + .13, 1);
  group.add(backing);
  const video = document.createElement("video");
  video.muted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  // Keep a real playing element in the document; display:none may throttle media
  // in headset browsers. This element isn't the browser's visible video screen.
  Object.assign(video.style, { position: "fixed", width: "1px", height: "1px",
    pointerEvents: "none", bottom: "0" });
  document.body.appendChild(video);
  const material = new MeshBasicMaterial({ toneMapped: false });
  const screen = new Mesh(geometry, material);
  screen.position.z = .002;
  screen.visible = false;
  screen.renderOrder = 1;
  group.add(screen);
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 96;
  const context = canvas.getContext("2d");
  const titleTexture = new CanvasTexture(canvas);
  titleTexture.colorSpace = SRGBColorSpace;
  const titleMaterial = new MeshBasicMaterial({ map: titleTexture,
    toneMapped: false });
  const title = new Mesh(geometry, titleMaterial);
  title.scale.set(width, .11, 1);
  title.position.set(0, height / 2, .003);
  title.renderOrder = 2;
  group.add(title);

  let source: XrVideoSource | null = null;
  let texture: VideoTexture | null = null;
  let frameCallback: number | null = null;
  let lastFrameAt = -Infinity;
  let lastMediaTime = -1;
  let lastTitle = "";
  let size = "";
  let qualityFrames = 0;
  let callbackFrames = 0;
  let live = false;
  let playError: string | null = null;
  let loadedCurrentSource = false;
  const onLoadedData = () => { loadedCurrentSource = video.srcObject != null; };
  video.addEventListener("loadeddata", onLoadedData);
  const frame = (now: number) => {
    callbackFrames++;
    lastFrameAt = now;
    frameCallback = video.requestVideoFrameCallback(frame);
  };
  const resetTexture = () => {
    texture?.dispose();
    texture = new VideoTexture(video);
    texture.colorSpace = SRGBColorSpace;
    // Bootstrap the first upload even when an offscreen video's rVFC is delayed.
    texture.needsUpdate = true;
    material.map = texture;
    material.needsUpdate = true;
  };
  const detach = () => {
    if (frameCallback != null) video.cancelVideoFrameCallback(frameCallback);
    frameCallback = null;
    texture?.dispose();
    texture = null;
    material.map = null;
    video.pause();
    video.srcObject = null;
    screen.visible = false;
    lastFrameAt = -Infinity;
    lastMediaTime = -1;
    qualityFrames = 0;
    callbackFrames = 0;
    live = false;
    playError = null;
    loadedCurrentSource = false;
    size = "";
  };

  return {
    group,
    handle: title,
    highlight(on: boolean) { titleMaterial.color.set(on ? 0x7de5db : 0xffffff); },
    update(next: XrVideoSource, now: number) {
      if (source?.stream !== next.stream || source?.slot !== next.slot || source?.enabled !== next.enabled) {
        detach();
        if (next.enabled && next.stream?.getVideoTracks().length) {
          video.srcObject = next.stream;
          qualityFrames = typeof video.getVideoPlaybackQuality === "function"
            ? video.getVideoPlaybackQuality().totalVideoFrames : 0;
          resetTexture();
          if (typeof video.requestVideoFrameCallback === "function") {
            frameCallback = video.requestVideoFrameCallback(frame);
          }
          void video.play().catch((error: unknown) => {
            if (video.srcObject === next.stream) {
              playError = error instanceof Error ? error.message : String(error);
            }
          });
        }
      }
      source = { ...next };
      if (video.srcObject && video.readyState >= 2) {
        const frames = typeof video.getVideoPlaybackQuality === "function"
          ? video.getVideoPlaybackQuality().totalVideoFrames : 0;
        if (frames > 0 && frames !== qualityFrames) {
          qualityFrames = frames;
          lastFrameAt = now;
          if (texture) texture.needsUpdate = true;
        }
        // Rendering the first available frame breaks the dependency between
        // compositor-driven rVFC and a mesh that was hidden until rVFC arrived.
        if (loadedCurrentSource && lastFrameAt === -Infinity && video.videoWidth > 0) lastFrameAt = now;
      }
      if (typeof video.requestVideoFrameCallback !== "function" && video.currentTime !== lastMediaTime) {
        lastMediaTime = video.currentTime;
        if (video.readyState >= 2) lastFrameAt = now;
      }
      live = next.enabled && video.srcObject != null && video.readyState >= 2
        && video.videoWidth > 0 && video.videoHeight > 0
        && (next.stream?.getVideoTracks().some((t) => t.readyState === "live" && !t.muted) ?? false)
        && now - lastFrameAt <= XR_VIDEO_STALE_MS;
      screen.visible = live;
      if (live) {
        const nextSize = `${video.videoWidth}x${video.videoHeight}`;
        if (size !== nextSize) {
          // VideoTexture dimensions are immutable after the first GPU upload.
          resetTexture();
          size = nextSize;
        }
        const ratio = video.videoWidth / video.videoHeight;
        const fittedHeight = Math.min(height - .12, width / ratio);
        screen.scale.set(fittedHeight * ratio, fittedHeight, 1);
        screen.position.y = -.05;
      }
      const text = `${next.label} · ${live ? "видео" : !next.stream?.getVideoTracks().length
        ? "нет потока" : next.enabled ? "нет свежих кадров" : "выключена"}`;
      if (text !== lastTitle && context) {
        lastTitle = text;
        context.fillStyle = "#17212b";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = live ? "#ffffff" : "#ffcf80";
        context.font = "32px sans-serif";
        context.textBaseline = "middle";
        context.fillText(text, 18, 48, 988);
        titleTexture.needsUpdate = true;
      }
    },
    diagnostics(): XrVideoDiagnostics {
      return { slot: source?.slot ?? -1, label: source?.label ?? "Ожидание камер",
        hasStream: video.srcObject != null, readyState: video.readyState, paused: video.paused,
        width: video.videoWidth, height: video.videoHeight,
        frames: Math.max(qualityFrames, callbackFrames), live,
        error: playError ?? video.error?.message ?? null };
    },
    dispose() {
      detach();
      video.removeEventListener("loadeddata", onLoadedData);
      video.remove();
      geometry.dispose();
      material.dispose();
      backingMaterial.dispose();
      titleMaterial.dispose();
      titleTexture.dispose();
      group.removeFromParent();
    },
  };
}

export function createXrVideoPanels(getSources: () => XrVideoSource[],
  options: { editable?: boolean; storageKey?: string } = {}) {
  const group = new Group();
  const panels = [makePanel(1.3, .96, 0, .18), makePanel(.64, .48, -.35, -.61),
    makePanel(.64, .48, .35, -.61)];
  for (const panel of panels) group.add(panel.group);
  let anchored = false;
  let sources: XrVideoSource[] = [];
  const layout = createWindowLayout(group, () => panels.flatMap((panel, i) =>
    i < sources.length ? [{ ...panel, slot: sources[i]!.slot }] : []), options);
  return {
    group,
    update(now: number, pose: XRViewerPose | undefined, frame?: XRFrame, refSpace?: XRReferenceSpace) {
      group.visible = pose != null;
      if (!pose) { layout.update(now, frame, refSpace, pose); return; }
      if (!anchored) {
        const { position: p, orientation: q } = pose.transform;
        group.position.set(p.x, p.y, p.z);
        // Place upright windows once in front of the operator. Afterwards head
        // translation and rotation do not change any window's room coordinates.
        const forward = new Vector3(0, 0, -1).applyQuaternion(new Quaternion(q.x, q.y, q.z, q.w));
        group.quaternion.setFromAxisAngle(new Vector3(0, 1, 0), Math.atan2(-forward.x, -forward.z));
        anchored = true;
      }
      sources = getSources().slice(0, panels.length);
      layout.sync();
      panels.forEach((panel, i) => {
        panel.group.visible = i === 0 || i < sources.length;
        panel.update(sources[i] ?? { slot: -1, label: "Ожидание камер", stream: null, enabled: false }, now);
      });
      layout.update(now, frame, refSpace, pose);
    },
    diagnostics() {
      return panels.filter((panel) => panel.group.visible).map((panel) => panel.diagnostics());
    },
    dispose() {
      layout.dispose();
      for (const panel of panels) panel.dispose();
      group.removeFromParent();
    },
  };
}
